import {
  type IDedicatedException,
  type IDedicatedResourceRef,
  type ISchedule,
  type IWorkingLocationPresenceType,
  type Office,
  WL_NOT_AVAILABLE,
  WL_PRESENCE_OFFICE,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE
} from "@gettactic/api";
import { todayInTimeZone } from "@gettactic/helpers/src/date-time/todayInTimeZone";
import { isWorkingLocationOffice } from "@/lib/utils";

/** Whether one of the releases covers the day (`yyyy-MM-dd`, inclusive). */
export const isReleasedOn = (
  releases: readonly IDedicatedException[],
  date: string
): boolean =>
  releases.some(
    (release) => release.starts_on <= date && date <= release.ends_on
  );

export interface DeskToRelease {
  id: string;
  name: string;
  /** The desk's office, whose calendar the released days belong to. */
  officeId: string | null;
}

/**
 * The dedicated desk worth offering to release with a location change: the
 * person is leaving the desk's office that day, for another office or for
 * no office at all, the day is not past, and the desk is not released yet.
 */
export const deskToRelease = (
  desk: IDedicatedResourceRef | null | undefined,
  /** What the day was before the change: only an office day is at the desk. */
  previousLocation: IWorkingLocationPresenceType | null | undefined,
  previousOfficeId: string | null | undefined,
  newLocationId: IWorkingLocationPresenceType | string,
  releases: readonly IDedicatedException[],
  date: string,
  today: string
): DeskToRelease | null => {
  if (!desk?.office_id || date < today) {
    return null;
  }
  // An agenda day keeps the office it last held after it turns remote or out
  // of office, so the id alone would call a remote day a day at the desk, and
  // leaving remote for anything else would offer to free a desk the person
  // had never taken that day.
  if (previousLocation !== WL_PRESENCE_OFFICE) {
    return null;
  }
  if (previousOfficeId !== desk.office_id) {
    return null;
  }
  if (
    isWorkingLocationOffice(newLocationId) &&
    newLocationId === desk.office_id
  ) {
    return null;
  }
  if (isReleasedOn(releases, date)) {
    return null;
  }
  return { id: desk.id, name: desk.name, officeId: desk.office_id ?? null };
};

/** The office a reservation sits in, from its resource or its first slot. */
export const getReservationOfficeId = (schedule: ISchedule): string | null =>
  schedule.resource?.office_id ??
  schedule.slots?.[0]?.resource?.office_id ??
  null;

/**
 * Today on the office's calendar, `yyyy-MM-dd`, falling back to the viewer's
 * own when the office is unknown. The days a desk is released are the office's
 * days, so "is that day past" has to be asked there.
 */
export const officeToday = todayInTimeZone;

/**
 * The reservations that are not where the person will be: everything in a
 * different office than the one picked, and every reservation at all when
 * the new location is not an office (remote, out of office, unavailable).
 *
 * Virtual reservations are left out: they hold no resource, so they are
 * nowhere in particular, and the API has no way to cancel one.
 */
export const filterReservationsElsewhere = (
  schedules: readonly ISchedule[],
  newLocationId: IWorkingLocationPresenceType | string
): ISchedule[] => {
  const newOfficeId = isWorkingLocationOffice(newLocationId)
    ? newLocationId
    : null;
  return schedules.filter((schedule) => {
    if (schedule.is_virtual) {
      return false;
    }
    // Not going to an office at all, so everything booked is somewhere the
    // person will not be - including a reservation whose office cannot be
    // told, which comparing ids would have quietly counted as "already
    // where you're going" and left on the calendar.
    if (newOfficeId === null) {
      return true;
    }
    return getReservationOfficeId(schedule) !== newOfficeId;
  });
};

/** "at Lisbon", "working remotely"…, for the prompt's sentence. */
export const describeWorkingLocation = (
  newLocationId: IWorkingLocationPresenceType | string,
  offices: readonly Pick<Office, "id" | "name">[]
): string => {
  if (isWorkingLocationOffice(newLocationId)) {
    const office = offices.find((x) => x.id === newLocationId);
    return office ? `at ${office.name}` : "at another office";
  }
  switch (newLocationId) {
    case WL_PRESENCE_REMOTE:
      return "working remotely";
    case WL_PRESENCE_OOO:
      return "out of office";
    case WL_NOT_AVAILABLE:
      return "unavailable";
    default:
      return "without a working location";
  }
};
